/**
 * Übersetzt und ordnet die Bausteine des MJML-Plugins.
 *
 * Das Plugin liefert seine Bausteine auf Englisch („1 Column", „Group Social")
 * und ohne Symbole. In einer Kanzlei arbeitet niemand mit „Spacer" — und ein
 * Baustein, dessen Name man erst übersetzen muss, wird nicht benutzt.
 *
 * Einige Bausteine werden ganz entfernt: Was in einem Newsletter einer
 * Steuerberatung nichts verloren hat oder wofür es einen besseren
 * Kanzlei-Baustein gibt, steht sonst nur im Weg.
 */

import { symbolFuer, SYMBOLE } from './baustein-symbole.js';

/**
 * Deutsche Namen für die Bausteine des Plugins.
 * Schlüssel sind die Kennungen, die das Plugin vergibt.
 * @type {Record<string, string>}
 */
const NAMEN = {
  'mj-1-column': 'Eine Spalte',
  'mj-2-columns': 'Zwei Spalten',
  'mj-3-columns': 'Drei Spalten',
  'mj-text': 'Text',
  'mj-button': 'Schaltfläche',
  'mj-image': 'Bild',
  'mj-divider': 'Trennlinie',
  'mj-spacer': 'Abstand',
  'mj-social-group': 'Soziale Netzwerke',
  'mj-social-element': 'Netzwerk-Symbol',
  'mj-navbar': 'Menüleiste',
  'mj-navbar-link': 'Menüpunkt',
  'mj-hero': 'Aufmacher',
  'mj-wrapper': 'Umschlag',
  'mj-section': 'Abschnitt',
  'mj-column': 'Spalte',
  'mj-table': 'Tabelle',
  'mj-raw': 'Eigener HTML-Code',
};

/**
 * Bausteine, die aus der Auswahl verschwinden.
 *
 * Menüleisten und Netzwerk-Symbole gehören in einen Newsletter einer
 * Steuerberatung nicht hinein; „Eigener HTML-Code" umgeht das Prüf-Gate und
 * ist damit genau der Weg, auf dem Outlook-Fehler wieder hereinkommen.
 */
const ENTFERNEN = new Set([
  'mj-navbar',
  'mj-navbar-link',
  'mj-social-group',
  'mj-social-element',
  'mj-hero',
  'mj-raw',
  'mj-wrapper',
]);

/** Symbole für die verbleibenden Plugin-Bausteine. */
const PLUGIN_SYMBOLE = {
  'mj-1-column': 'kanzlei-absatz',
  'mj-2-columns': 'kanzlei-zwei-spalten',
  'mj-text': 'kanzlei-absatz',
  'mj-button': 'kanzlei-schaltflaeche',
  'mj-image': 'kanzlei-bild',
  'mj-divider': 'kanzlei-trenner',
};

/**
 * Räumt die Bausteinauswahl auf: übersetzt, bebildert, entfernt Überflüssiges.
 *
 * @param {any} editor
 */
export function bausteineAufraeumen(editor) {
  const verwaltung = editor.BlockManager;

  for (const baustein of [...verwaltung.getAll().models]) {
    const kennung = baustein.getId();

    // Kanzlei-Bausteine sind bereits deutsch und bebildert.
    if (kennung.startsWith('kanzlei-')) continue;

    if (ENTFERNEN.has(kennung)) {
      verwaltung.remove(kennung);
      continue;
    }

    const name = NAMEN[kennung];
    if (name) baustein.set('label', name);

    if (!baustein.get('media')) {
      const vorbild = PLUGIN_SYMBOLE[/** @type {keyof typeof PLUGIN_SYMBOLE} */ (kennung)];
      baustein.set('media', symbolFuer(vorbild ?? kennung));
    }

    // In eine eigene Kategorie, damit die Kanzlei-Bausteine oben stehen.
    baustein.set('category', { id: 'grundelemente', label: 'Grundelemente', open: false });
  }

  // Sicherstellen, dass für jeden Kanzlei-Baustein ein Symbol vorliegt.
  for (const kennung of Object.keys(SYMBOLE)) {
    const baustein = verwaltung.get(kennung);
    if (baustein && !baustein.get('media')) baustein.set('media', symbolFuer(kennung));
  }
}
